import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  fetchBiasReports,
  fetchGroupPrompts,
  fetchGroupResponses,
  fetchPrompts,
  fetchResponses,
  LLMGroupResponseType,
  LLMResponseType,
  PromptType,
  selectors,
} from 'state';

export const LLM_RESPONSE_PLACEHOLDER_PREFIX =
  'Response started generating at UTC';

export type PlayerAIReportOption = {
  promptId: number;
  promptName: string;
  responseId: number;
  response: string;
};

export type GroupAIReportOption = {
  promptId: number;
  promptName: string;
  responseId: number;
  groupReportName: string;
  response: string;
};

export const isCompletedLLMResponse = (
  response: Pick<LLMResponseType, 'response'>
) => !response.response.startsWith(LLM_RESPONSE_PLACEHOLDER_PREFIX);

export const isCompletedLLMGroupResponse = (
  response: Pick<LLMGroupResponseType, 'response'>
) => !response.response.startsWith(LLM_RESPONSE_PLACEHOLDER_PREFIX);

export const getCompletedPlayerAIReportOptions = (
  promptsObj: Record<number, PromptType>,
  responsesObj: Record<number, LLMResponseType>,
  playerId: number
): PlayerAIReportOption[] => {
  const latestResponsesByPrompt = new Map<number, LLMResponseType>();

  Object.values(responsesObj)
    .sort((a, b) => b.id - a.id)
    .forEach(response => {
      if (response.player !== playerId || !isCompletedLLMResponse(response)) {
        return;
      }

      if (!latestResponsesByPrompt.has(response.prompt)) {
        latestResponsesByPrompt.set(response.prompt, response);
      }
    });

  return Array.from(latestResponsesByPrompt.values())
    .map(response => {
      const prompt = promptsObj[response.prompt];

      if (!prompt) {
        return null;
      }

      return {
        promptId: prompt.id,
        promptName: prompt.name,
        responseId: response.id,
        response: response.response,
      };
    })
    .filter((option): option is PlayerAIReportOption => option != null)
    .sort((a, b) => b.promptId - a.promptId);
};

export const useTrainerAIReportData = (sessionId: number) => {
  const dispatch = useDispatch();
  const promptsObj = useSelector(selectors.prompts);
  const responses = useSelector(selectors.llmResponses);

  useEffect(() => {
    dispatch(fetchPrompts({ session_id: sessionId, type: 'individual' }));
    dispatch(fetchResponses({ session_id: sessionId }));
  }, [dispatch, sessionId]);

  useEffect(() => {
    const finishedPromptIds = new Set(
      Object.values(responses)
        .filter(isCompletedLLMResponse)
        .map(response => response.prompt)
    );
    const hasMissingFinishedPrompt = [...finishedPromptIds].some(
      promptId => promptsObj[promptId] === undefined
    );
    const hasStaleVisiblePrompt = Object.values(promptsObj).some(
      prompt => !finishedPromptIds.has(prompt.id)
    );

    if (hasMissingFinishedPrompt || hasStaleVisiblePrompt) {
      dispatch(fetchPrompts({ session_id: sessionId, type: 'individual' }));
    }
  }, [dispatch, sessionId, promptsObj, responses]);

  return { promptsObj, responses };
};

/** Loads all bias reports for a session (one per player). Live updates arrive
 *  via the `bias_report_generated` / `bias_report_error` socket listeners. */
export const useTrainerBiasReportData = (sessionId: number) => {
  const dispatch = useDispatch();
  const biasReports = useSelector(selectors.biasReports);

  useEffect(() => {
    if (sessionId) {
      dispatch(fetchBiasReports({ session_id: sessionId }));
    }
  }, [dispatch, sessionId]);

  return { biasReports };
};

export const getCompletedGroupAIReportOptions = (
  promptsObj: Record<number, PromptType>,
  responsesObj: Record<number, LLMGroupResponseType>,
  sessionId: number
): GroupAIReportOption[] => {
  const latestResponsesByIdentity = new Map<string, LLMGroupResponseType>();

  Object.values(responsesObj)
    .sort((a, b) => b.id - a.id)
    .forEach(response => {
      if (
        response.session !== sessionId ||
        !isCompletedLLMGroupResponse(response)
      ) {
        return;
      }

      const identity = `${response.prompt}:${response.group_report_name}`;
      if (!latestResponsesByIdentity.has(identity)) {
        latestResponsesByIdentity.set(identity, response);
      }
    });

  return Array.from(latestResponsesByIdentity.values())
    .map(response => {
      const prompt = promptsObj[response.prompt];

      if (!prompt) {
        return null;
      }

      return {
        promptId: prompt.id,
        promptName: prompt.name,
        responseId: response.id,
        groupReportName: response.group_report_name,
        response: response.response,
      };
    })
    .filter((option): option is GroupAIReportOption => option != null)
    .sort((a, b) => b.responseId - a.responseId);
};

export const useTrainerGroupAIReportData = (sessionId: number) => {
  const dispatch = useDispatch();
  const promptsObj = useSelector(selectors.groupPrompts);
  const responses = useSelector(selectors.llmGroupResponses);

  useEffect(() => {
    dispatch(fetchGroupPrompts({ session_id: sessionId, type: 'group' }));
    dispatch(fetchGroupResponses({ session_id: sessionId }));
  }, [dispatch, sessionId]);

  useEffect(() => {
    const finishedPromptIds = new Set(
      Object.values(responses)
        .filter(isCompletedLLMGroupResponse)
        .map(response => response.prompt)
    );
    const hasMissingFinishedPrompt = [...finishedPromptIds].some(
      promptId => promptsObj[promptId] === undefined
    );
    const hasStaleVisiblePrompt = Object.values(promptsObj).some(
      prompt => !finishedPromptIds.has(prompt.id)
    );

    if (hasMissingFinishedPrompt || hasStaleVisiblePrompt) {
      dispatch(fetchGroupPrompts({ session_id: sessionId, type: 'group' }));
    }
  }, [dispatch, sessionId, promptsObj, responses]);

  return { promptsObj, responses };
};
